import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Filter } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { ProductGrid, FilterSidebar } from '@/components/products'
import { useProducts } from '@/hooks/useProducts'
import { useFilterStore } from '@/stores/filterStore'
import type { FilterType } from '@/types'

export function ProductsPage() {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const { filters } = useFilterStore()

  // Get all products without filters to calculate available filter values
  const { data: allProducts } = useProducts({})

  // Get filtered products
  const { data: products, isLoading } = useProducts({ filters })

  // Calculate available filter values from all products
  const availableFilterValues = useMemo(() => {
    const values: Record<FilterType, Set<string>> = {
      water_tightness: new Set(),
      fire_class: new Set(),
      material: new Set(),
      product: new Set(),
    }

    allProducts?.forEach(product => {
      product.filters?.forEach(filter => {
        if (filter.filter_type in values) {
          values[filter.filter_type as FilterType].add(filter.filter_value)
        }
      })
    })

    return {
      water_tightness: Array.from(values.water_tightness),
      fire_class: Array.from(values.fire_class),
      material: Array.from(values.material),
      product: Array.from(values.product),
    }
  }, [allProducts])

  return (
    <>
      <SEO pageKey="products" fallback={{ title: 'Marine Equipment & Products - EngiMarineX Catalog' }} />
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              All Products
            </h1>
            <p className="text-lg text-secondary-300 leading-relaxed">
              Comprehensive range of marine equipment including doors, hatches,
              valves, ventilation systems, and deck equipment. All products
              certified by leading classification societies.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Products */}
      <section className="py-12 lg:py-16">
        <Container>
          {/* Mobile Filter Button */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setIsMobileFilterOpen(true)}
              className="btn-outline w-full justify-center"
            >
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </button>
          </div>

          <div className="flex gap-8">
            {/* Sidebar */}
            <FilterSidebar
              isMobileOpen={isMobileFilterOpen}
              onMobileClose={() => setIsMobileFilterOpen(false)}
              availableFilterValues={availableFilterValues}
            />

            {/* Products */}
            <div className="flex-1 min-w-0">
              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-secondary-600">
                  {isLoading ? (
                    'Loading...'
                  ) : (
                    <>
                      <span className="font-semibold text-secondary-900">
                        {products?.length || 0}
                      </span>{' '}
                      products found
                    </>
                  )}
                </p>
              </div>

              <ProductGrid
                products={products || []}
                isLoading={isLoading}
              />
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
